import type { AfterContentChecked, OnDestroy, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  inject,
} from '@angular/core';
import type { LocalStorageTypes } from '@freelancer/local-storage';
import { LocalStorage } from '@freelancer/local-storage';
import { ButtonComponent } from '@freelancer/ui/button';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { FlexAlign, FlexDirection, FlexJustify } from '@freelancer/ui/flex';
import { IconModule } from '@freelancer/ui/icon';
import { IconSize } from '@freelancer/ui/icon';
import { RibbonComponent } from '@freelancer/ui/ribbon';
import { isDefined } from '@freelancer/utils';
import type { Subscription } from 'rxjs';
import { distinctUntilChanged, map, tap } from 'rxjs/operators';
import { CardHeaderRightComponent } from './card-header-right.component';
import { CardHeaderSecondaryComponent } from './card-header-secondary.component';
import { CardHeaderTitleComponent } from './card-header-title.component';
import {
  CardBorder,
  CardBorderRadius,
  CardHeaderButton,
  CardLevel,
  CardSize,
} from './card.types';

@Component({
  standalone: true,
  selector: 'fl-card',
  imports: [ButtonComponent, IconModule],
  template: `
    @if ((localStorageflCardId && finishedLoading) || !localStorageflCardId) {
      <div
        class="CardContainer"
        [class.DisableFullHeight]="disableFullHeight"
        [attr.data-closeable]="closeable"
      >
        @if (closeable && !ribbon) {
          <fl-icon
            class="CardCloseIcon"
            i18n-label="Card close icon label"
            label="Close card"
            [name]="'ui-close'"
            [size]="IconSize.MID"
            (click)="handleCloseClick()"
            [useIconFont]="true"
          ></fl-icon>
        }
        @if (cardHeaderTitle || cardHeaderButton || cardHeaderSecondary) {
          <div
            class="CardHeader"
            [attr.data-closeable]="closeable"
            [attr.data-size]="size"
            [attr.data-expandable]="expandable"
            [class.HeaderIsExpanded]="expandable && expanded"
            [class.mobile:hide]="hideHeaderMobile"
            [class.mobile:show]="showHeaderMobile"
            (click)="handleTitleClick()"
          >
            @if (cardHeaderTitle || cardHeaderButton) {
              <div
                class="CardHeaderInner"
                [attr.data-size]="size"
                [attr.data-button-available]="cardHeaderButton && true"
              >
                <h2
                  class="CardHeading"
                  [class.text-mid]="size == 'mid'"
                  [class.text-body-16]="!(size == 'mid')"
                >
                  <ng-content select="fl-card-header-title"></ng-content>
                </h2>
                @if (cardHeaderRight || cardHeaderButton) {
                  <div class="CardHeaderRight">
                    @if (cardHeaderButton) {
                      <fl-button
                        [color]="
                          cardHeaderButton.color
                            ? cardHeaderButton.color
                            : ButtonColor.SECONDARY
                        "
                        [size]="
                          size == 'mid' ? ButtonSize.SMALL : ButtonSize.MINI
                        "
                        [link]="cardHeaderButton.link"
                        [linkActive]="cardHeaderButton.linkActive"
                        [fragment]="cardHeaderButton.fragment"
                        [queryParams]="cardHeaderButton.queryParams"
                        [busy]="cardHeaderButton.busy"
                        [linkActiveOptions]="cardHeaderButton.linkActiveOptions"
                        class="{{
                          closeable && !cardHeaderRight
                            ? 'mr-xsmall'
                            : 'mr-none'
                        }} {{
                          closeable && !cardHeaderRight
                            ? 'tablet:mr-xxsmall'
                            : 'tablet:mr-none'
                        }}"
                        (click)="handleButtonClick()"
                      >
                        {{ cardHeaderButton.text }}
                      </fl-button>
                    }
                    <ng-content select="fl-card-header-right"></ng-content>
                  </div>
                }
                @if (expandable) {
                  <fl-icon
                    class="ExpandIcon"
                    [class.IsExpanded]="expanded"
                    [name]="'ui-chevron-down'"
                    [size]="IconSize.SMALL"
                  >
                  </fl-icon>
                }
              </div>
            }
            <ng-content select="fl-card-header-secondary"></ng-content>
          </div>
        }
        <div
          class="CardBody"
          [attr.data-flex-direction]="flexDirection"
          [attr.data-flex-direction-tablet]="flexDirectionTablet"
          [attr.data-flex-direction-desktop]="flexDirectionDesktop"
          [attr.data-flex-align]="flexAlign"
          [attr.data-flex-justify]="flexJustify"
          [attr.data-size]="size"
          [attr.data-closeable]="closeable"
          [attr.data-edge-to-edge]="edgeToEdge"
          [attr.data-edge-to-edge-desktop]="edgeToEdgeDesktop"
          [attr.data-ribbon]="ribbon ? true : false"
          [class.IsHidden]="expandable && !expanded"
        >
          <ng-content></ng-content>
        </div>
      </div>
    }
  `,
  styleUrls: ['./card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CardComponent implements OnInit, OnDestroy, AfterContentChecked {
  private localStorage = inject(LocalStorage);
  private cdRef = inject(ChangeDetectorRef);

  @HostBinding('attr.data-disable-boxshadow')
  @Input()
  disableBoxshadow = false;

  /** Forces the card to have a full 100% width of its parent container */
  @HostBinding('class.MaxContent')
  @Input()
  maxContent = true;

  /** Defines the level of the box shadow of the card */
  @HostBinding('attr.data-level')
  @Input()
  level: CardLevel = 'primary';

  /** Defines the level of the box shadow of the card */
  @HostBinding('attr.data-border')
  @Input()
  border: CardBorder = 'none';

  /** Makes the card appear to be clickable */
  @HostBinding('class.CardClickable')
  @Input()
  clickable = false;

  @Input() flexDirection: FlexDirection = 'column';
  @Input() flexDirectionTablet?: FlexDirection;
  @Input() flexDirectionDesktop?: FlexDirection;

  @Input() flexAlign: FlexAlign;
  @Input() flexJustify: FlexJustify;

  /** Makes the card expandable */
  /** Please be careful when handle click event if you have clickable and expandable enabled at the same time.
   * It might conflict if the user clicks the card header */
  @Input() expandable = false;

  /** Allow user to specify if they want the card to initally render in expanded state or not */
  @Input() initialExpandedState = false;

  @HostBinding('attr.data-radius')
  @Input()
  borderRadius: CardBorderRadius = 'mid';

  @Input() cardHeaderButton?: CardHeaderButton;

  /** Removes padding inside the card component */
  @Input() edgeToEdge = false;

  /** Applies edgeToEdge only to desktop screen size */
  @Input() edgeToEdgeDesktop = false;

  /** Defines the padding level inside the card */
  @Input() size: CardSize = 'mid';

  /** Local storage reference to allow expanded state to be tracked */
  @Input() localStorageflCardId?: string;

  /** Allow user to close card */
  /** Not available if ribbon is present */
  @Input() closeable = false;

  @HostBinding('class.IsHidden') cardHidden = false;

  @Input() hideHeaderMobile = false;

  @Input() showHeaderMobile = false;

  /** Disable height 100% so the radio button on the radio card can be centered*/
  @Input() disableFullHeight = false;

  @Output() closeClicked = new EventEmitter<void>();
  @Output() buttonClicked = new EventEmitter<void>();

  @ContentChild(CardHeaderTitleComponent)
  cardHeaderTitle: CardHeaderTitleComponent;
  @ContentChild(CardHeaderRightComponent)
  cardHeaderRight: CardHeaderRightComponent;
  @ContentChild(CardHeaderSecondaryComponent)
  cardHeaderSecondary: CardHeaderSecondaryComponent;
  @ContentChild(RibbonComponent) ribbon: RibbonComponent;

  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  IconSize = IconSize;
  expanded: boolean;
  expandedLocalStorageState:
    | LocalStorageTypes['flCardsExpandedStatus']
    | undefined;
  // if we are tracking expanded state, this ensures we wait for localStorage expanded state
  // to load before rendering the card
  finishedLoading = false;

  private isExpandedSubscription?: Subscription;

  ngOnInit(): void {
    if (this.expandable && this.localStorageflCardId) {
      const cardId = this.localStorageflCardId;

      const isExpanded$ = this.localStorage.get('flCardsExpandedStatus').pipe(
        tap(state => {
          // get the object containing all expanded states for any tracked fl-cards in the webapp, so we can add the state of
          // this card to the object later, whenever the expand chevron is clicked
          if (state) {
            this.expandedLocalStorageState = state;
          } else {
            this.expandedLocalStorageState = {};
          }
        }),
        map(flCardsExpandedStates =>
          !flCardsExpandedStates || !isDefined(flCardsExpandedStates[cardId])
            ? this.initialExpandedState
            : flCardsExpandedStates[cardId],
        ),
        distinctUntilChanged(),
      );

      this.isExpandedSubscription = isExpanded$.subscribe(isExpanded => {
        this.expanded = isExpanded;
        this.finishedLoading = true;
        this.cdRef.markForCheck();
      });
    } else {
      this.expanded = this.initialExpandedState;
    }
  }

  handleButtonClick(): void {
    this.buttonClicked.emit();
  }

  handleTitleClick(): Promise<void> | undefined {
    if (!this.expandable) {
      return;
    }
    // update local storage, inserting new expanded state of this card
    // into object holding all cards states
    if (this.expandable && this.localStorageflCardId) {
      const newLocalStorageState = {
        ...this.expandedLocalStorageState,
        [this.localStorageflCardId]: !this.expanded,
      };
      return this.localStorage.set(
        'flCardsExpandedStatus',
        newLocalStorageState,
      );
    }
    this.expanded = !this.expanded;
    this.cdRef.markForCheck();
  }

  handleCloseClick(): void {
    this.cardHidden = true;
    this.closeClicked.emit();
    this.cdRef.markForCheck();
  }

  ngAfterContentChecked(): void {
    // With OnPush change detection, we need to mark for check when content children
    // (like fl-card-header-right) appear/disappear due to *ngIf changes
    this.cdRef.markForCheck();
  }

  ngOnDestroy(): void {
    this.isExpandedSubscription?.unsubscribe();
  }
}
